import { useState, useEffect, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getNotificationPreferences,
  updateNotificationPreferences,
  type NotificationPreferences,
} from '../api/settings';

/**
 * Hook to fetch and manage user notification preferences
 * Uses TanStack Query for caching and state management
 * Tracks local changes that can be saved with the main form
 */
export function useNotificationPreferences() {
  const queryClient = useQueryClient();

  // Local state for pending changes (before save)
  const [localPreferences, setLocalPreferences] = useState<NotificationPreferences | null>(null);
  const [isDirty, setIsDirty] = useState(false);

  // Fetch notification preferences query
  const {
    data: serverPreferences,
    isPending: isLoading,
    error,
    refetch,
  } = useQuery<NotificationPreferences, Error>({
    queryKey: ['notificationPreferences'],
    queryFn: getNotificationPreferences,
    // Cache for 5 minutes
    staleTime: 5 * 60 * 1000,
    // Keep data in cache for 10 minutes
    gcTime: 10 * 60 * 1000,
  });

  // Initialize local state when server data loads
  useEffect(() => {
    if (serverPreferences && !isDirty) {
      setLocalPreferences(serverPreferences);
    }
  }, [serverPreferences, isDirty]);

  // Update notification preferences mutation
  const updatePreferencesMutation = useMutation<
    NotificationPreferences,
    Error,
    Partial<NotificationPreferences>
  >({
    mutationFn: updateNotificationPreferences,
    onSuccess: (data) => {
      // Update the cache with new preferences
      queryClient.setQueryData(['notificationPreferences'], data);
      setLocalPreferences(data);
      setIsDirty(false);
    },
  });

  // Helper to toggle a single preference locally (doesn't save immediately)
  const togglePreference = useCallback((key: keyof NotificationPreferences) => {
    setLocalPreferences((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        [key]: !prev[key],
      };
    });
    setIsDirty(true);
  }, []);

  // Save all pending changes
  const savePreferences = useCallback(() => {
    if (!localPreferences || !isDirty) return;
    updatePreferencesMutation.mutate(localPreferences);
  }, [localPreferences, isDirty, updatePreferencesMutation]);

  // Reset local changes to server state
  const resetPreferences = useCallback(() => {
    if (serverPreferences) {
      setLocalPreferences(serverPreferences);
      setIsDirty(false);
    }
  }, [serverPreferences]);

  return {
    // Data - use local preferences for display
    preferences: localPreferences,

    // Loading states
    isLoading,
    isUpdating: updatePreferencesMutation.isPending,

    // Dirty state - tracks if there are unsaved changes
    isDirty,

    // Errors
    error,
    updateError: updatePreferencesMutation.error,

    // Actions
    togglePreference,
    savePreferences,
    resetPreferences,
    refetch,

    // Success states
    isUpdateSuccess: updatePreferencesMutation.isSuccess,
  };
}
